'use client'

import { useState, useEffect } from 'react'
import { Bell, ChevronDown, BookIcon, PanelLeft } from 'lucide-react'
import { Button, DropdownMenu, Avatar, Dropdown, DropdownItem, DropdownTrigger, Modal, ModalContent, ModalHeader, ModalFooter, Input, ModalBody, addToast } from '@heroui/react'
import { ThemeToggle } from '../theme/theme-toggle'
import { RectangleIcon } from '../icons/common/rectangle'
import { EyeIcon } from '../icons/common/eye'
import { getCurrentUser, useUserStore } from '@/store/user'
import { updatePwd } from '@/apis/account'
import { useRouter } from 'next/navigation'

export default function TopNav({ title }: { title?: string }) {
	const [currentUserName, setCurrentUserName] = useState('')
	const [userInitials, setUserInitials] = useState('U')
	const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
	const [oldPassword, setOldPassword] = useState('')
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const router = useRouter()

	const handleLogout = () => {
		// 调用登出方法清除用户信息
		useUserStore.getState().logout()
		// 导航到登录页面
		router.push('/logout')
	}

	const handlePasswordChange = async () => {
		if (!oldPassword) {
			addToast({
				color: 'danger',
				title: 'Error',
				description: '请输入你的旧密码'
			})
			return
		}
		if (password !== confirmPassword) {
			addToast({
				color: 'danger',
				title: 'Error',
				description: '密码不一致'
			})
			return
		}

		try {
			// 使用新添加的updatePwd API
			const response = await updatePwd({
				oldPassword,
				newPassword: password
			})

			// 检查API返回的success字段
			if (response.success && response.code === 200) {
				addToast({
					title: 'Success',
					description: '密码修改成功',
					timeout: 3000
				})
				setOldPassword('')
				setPassword('')
				setConfirmPassword('')
				setIsPasswordModalOpen(false)
			} else {
				// 显示API返回的具体错误消息
				addToast({
					color: 'danger',
					title: 'Error',
					description: response.msg || '密码修改失败'
				})
			}
		} catch (error) {
			// 处理网络或其他错误
			addToast({
				color: 'danger',
				title: 'Error',
				description: '密码修改失败'
			})
			console.error('Password change failed:', error)
		}
	}

	useEffect(() => {
		const user = getCurrentUser()
		if (user) {
			// 使用昵称显示
			setCurrentUserName(user.nick_name || 'User')
			// 生成昵称每个单词的首字母作为头像
			const initials = user.nick_name
				? user.nick_name.split(' ').map(word => word[0]).join('').toUpperCase()
				: 'U'
			setUserInitials(initials)
		}
	}, [])

	const handleMenuToggle = () => {
		if (typeof window !== 'undefined' && (window as any).toggleMenuState) {
			; (window as any).toggleMenuState()
		}
	}

	const handleMobileMenuToggle = () => {
		if (typeof window !== 'undefined' && (window as any).setIsMobileMenuOpen) {
			const currentState = (window as any).isMobileMenuOpen || false
				; (window as any).setIsMobileMenuOpen(!currentState)
		}
	}

	return (
		<div>
			<div className="flex items-center justify-between h-full p-4 pl-2 lg:pr-6 lg:pl-4">
				{/* Left side - Menu toggle and Breadcrumbs */}
				<div className="flex items-center">
					{/* Desktop Menu Toggle */}
					{/* <div>{currentLabel}</div> */}
					{/* menuData */}
					<Button isIconOnly variant="light" size="sm" onPress={handleMenuToggle} className="hidden lg:flex" title="Toggle Menu">
						<PanelLeft className="h-4 w-4" />
					</Button>
					<h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{title}</h1>

					{/* Mobile Menu Toggle */}
					<Button
						variant="light"
						size="sm"
						isIconOnly
						onPress={handleMobileMenuToggle}
						className="lg:hidden p-2"
						title="Toggle Mobile Menu"
					>
						<PanelLeft className="h-4 w-4" />
					</Button>
				</div>

				{/* Right side - Actions and Profile */}
				<div className="flex items-center space-x-2">
					{/* Theme Toggle */}
					{/* <ThemeToggle /> */}

					{/* Profile Dropdown */}
					<Dropdown placement='bottom-end'>
						<DropdownTrigger>
							<div className='flex justify-center items-center cursor-pointer'>
								<Avatar className='w-7 h-7 mr-1.5' name={userInitials} />
								<span className='font-semibold hidden md:block'>{currentUserName}</span>
								<ChevronDown className='w-4 font-bold' />
							</div>
						</DropdownTrigger>
						<DropdownMenu aria-label='Profile Actions' variant='flat'>
							{/* <DropdownItem
								key='change-password'
								startContent={
									<div className='w-5 h-5 flex justify-center items-center'>
										<EyeIcon className='w-5 h-5' />
									</div>
								}
								onClick={() => setIsPasswordModalOpen(true)}
							>
								修改密码
							</DropdownItem> */}
							<DropdownItem
								key='logout'
								color='danger'
								startContent={
									<div className='w-5 h-5 flex justify-center items-center'>
										<RectangleIcon className='w-5 h-5' />
									</div>
								}
								onClick={handleLogout}
							>
								退出登录
							</DropdownItem>
						</DropdownMenu>
					</Dropdown>
				</div>
			</div>

			<Modal
				isOpen={isPasswordModalOpen}
				onClose={() => setIsPasswordModalOpen(false)}
				// title="修改密码"
				backdrop="blur"
			>
				<ModalContent className="sm:max-w-[400px]">
					<ModalHeader>
						<h3 className="text-lg font-medium">修改密码</h3>
					</ModalHeader>
					<ModalBody>
						<div className="space-y-6">
							<Input
								id="old-password"
								type="password"
								value={oldPassword}
								onChange={(e: any) => setOldPassword(e.target.value)}
								required
								className="w-full"
								label="旧密码"
								variant="bordered"
							/>

							<Input
								id="new-password"
								type="password"
								value={password}
								onChange={(e: any) => setPassword(e.target.value)}
								required
								className="w-full"
								label="新密码"
								variant="bordered"
							/>

							<Input
								id="confirm-password"
								type="password"
								value={confirmPassword}
								onChange={(e: any) => setConfirmPassword(e.target.value)}
								required
								className="w-full"
								label="确认新密码"
								variant="bordered"
							/>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button variant="light" onPress={() => setIsPasswordModalOpen(false)}>取消</Button>
						<Button onPress={handlePasswordChange}>确定</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</div>
	)
}
